import { InfoCircleOutlined } from "@ant-design/icons";
import { Node } from "@p0security/shared/graph/types";
import { ProviderOrAll } from "@p0security/shared/types/assessment";
import {
  AssessmentNodes,
  AssessmentSchema,
  AssessmentSchemaAttribute,
} from "@p0security/shared/types/assessment/data/index";
import { Descriptions, List, Typography } from "antd";
import { GraphTooltip } from "components/GraphTooltip";
import {
  Dictionary,
  capitalize,
  kebabCase,
  keys,
  omit,
  upperFirst,
} from "lodash";
import { useMemo } from "react";
import styled from "styled-components";

import { Timestamp } from "../cells/Timestamp";
import { NodeDescriptions } from "./NodeDescriptions";

type PropertyDatum = { key: string; label?: string; value: any; depth: number };

const { Item } = Descriptions;
const { Text } = Typography;

const PropertyDescriptions = styled.div`
  td:not(:hover) button.ant-typography-copy {
    visibility: hidden;
  }
`;

const innerLabelStyle = {
  fontWeight: "normal",
  fontStyle: "italic",
  fontSize: "small",
};

// flattens node data
function* generatePropertyData(data: {
  value: Dictionary<any>;
  schema?: Dictionary<AssessmentSchemaAttribute>;
  depth?: number;
}): Generator<PropertyDatum> {
  const { value, schema, depth: inputDepth } = data;
  const depth = inputDepth ?? 0;

  for (const key of keys(schema ?? omit(value, "key")).sort()) {
    const current = value[key];
    // handle primitive values, arrays
    if (typeof current !== "object" || Array.isArray(current)) {
      yield { key, value: current, depth };
      continue;
    }
    // handle nested objects recursively
    yield { key, value: "", depth };
    for (const result of generatePropertyData({
      value: current,
      depth: depth + 1,
    })) {
      const inner = capitalize(kebabCase(result.key).replaceAll("-", " "));
      yield { ...result, key: `${key}.${result.key}`, label: inner };
    }
  }

  // Always put 'key' at end
  if ("key" in value) {
    yield { key: "key", value: value.key, depth };
  }
}

const renderArrayItem = (item: string) => {
  return (
    <List.Item>
      <Text copyable>{String(item)}</Text>
    </List.Item>
  );
};

export const NodeProperties: React.FC<{
  node: Node<AssessmentNodes, keyof AssessmentNodes>;
  provider: ProviderOrAll;
}> = ({ node, provider }) => {
  const { key, data, type } = node;

  const items = useMemo(() => {
    if (!data) return [];

    const schema: Dictionary<AssessmentSchemaAttribute> =
      AssessmentSchema[type].attributes;
    const value = { ...(data ?? {}), key };
    const keyValueData = [...generatePropertyData({ value, schema })];
    return keyValueData.map((data) => {
      const { value } = data;

      // schemaItem may be undefined if key is not in schema (e.g. a key with depth > 0)
      const schemaItem = schema?.[data.key] as
        | AssessmentSchemaAttribute
        | undefined;
      const labeler = schemaItem?.label;
      const label =
        typeof labeler === "string"
          ? labeler
          : (labeler?.[provider] ?? data.label ?? data.key);

      const help = data.depth === 0 ? schemaItem?.help : undefined;

      const propLabel = (
        <>
          <div>
            {upperFirst(label)}
            {help && (
              <>
                &nbsp;
                <GraphTooltip title={help}>
                  <InfoCircleOutlined style={{ color: "DarkGray" }} />
                </GraphTooltip>
              </>
            )}
          </div>
          {label && (
            <div style={{ fontWeight: "normal", whiteSpace: "nowrap" }}>
              <Text type="secondary">{data.key}</Text>
            </div>
          )}
        </>
      );

      return (
        <Item
          key={data.key}
          label={propLabel}
          styles={{
            label: data.depth > 0 ? innerLabelStyle : undefined,
          }}
        >
          {value === undefined ? (
            <Text type="secondary">(No value)</Text>
          ) : Array.isArray(value) ? (
            value.length ? (
              <List
                dataSource={value.map((item) => item.toString())}
                renderItem={renderArrayItem}
              />
            ) : (
              <Text type="secondary">(No data)</Text>
            )
          ) : schemaItem?.type === "date" && typeof value === "number" ? (
            <Timestamp at={value} copyable />
          ) : (
            <>
              <Text copyable>{String(value)}</Text>
            </>
          )}
        </Item>
      );
    });
  }, [data, key, provider, type]);

  return (
    <PropertyDescriptions>
      <NodeDescriptions theme="tight">{...items}</NodeDescriptions>
    </PropertyDescriptions>
  );
};
